'use client';

import type { SavedListing } from '@/src/components/features/property/contexts/SavedListingsContext';
import type { HotpadsClickstreamListing } from '@/src/analytics';
import { toHotpadsClickstreamListing } from '@/src/analytics/hotpads/track';

export type ListingLike = {
  maloneLotIdEncoded?: string;
  aliasEncoded?: string;
  userItemTypes?: Array<string>;
  uriMalone?: string;
  uriV2?: string;
  displayName?: string;
  title?: string;
  previewPhoto?: { url?: string };
  medPhotoUrl?: string;
  medPhotoUrls?: Array<string>;
  pricing?: { summary?: { priceLow?: number; priceHigh?: number } };
  listingMinMaxPriceBeds?: { minPrice?: number };
  bedText?: string;
  bathText?: string;
  address?: { neighborhood?: string; street?: string; city?: string; state?: string };
  geo?: { lat?: number; lon?: number };
};

export type ListingActionAnalyticsContext = {
  triggerLocation: string;
  triggerObject: string;
  listing?: HotpadsClickstreamListing | null;
};

export type SaveClickstreamContext = {
  triggerLocation:
    | 'home_details'
    | 'home_details_unit_modal'
    | 'search_results'
    | 'search_results_list'
    | 'search_results_map'
    | 'search_results_feed'
    | 'hp_homepage'
    | 'user_item_list_page'
    | 'user_item_viewed_page'
    | 'user_item_hidden_page';
  triggerObject: 'property_card' | 'home_details_component|action_bar' | 'home_details_component|recently_viewed';
};

export const SAVE_CLICKSTREAM_CONTEXTS = {
  feed: { triggerLocation: 'search_results_feed', triggerObject: 'property_card' },
  homepage: { triggerLocation: 'hp_homepage', triggerObject: 'property_card' },
  recentlyViewed: { triggerLocation: 'home_details', triggerObject: 'home_details_component|recently_viewed' },
  favorite: { triggerLocation: 'user_item_list_page', triggerObject: 'property_card' },
  viewed: { triggerLocation: 'user_item_viewed_page', triggerObject: 'property_card' },
  hidden: { triggerLocation: 'user_item_hidden_page', triggerObject: 'property_card' },
} as const satisfies Record<string, SaveClickstreamContext>;

export const SHARE_CLICKSTREAM_CONTEXTS = {
  hdp: {
    triggerLocation: 'home_details',
    triggerObject: 'home_details_component|action_bar',
  },
  unitModal: {
    triggerLocation: 'home_details_unit_modal',
    triggerObject: 'home_details_component|action_bar',
  },
  list: {
    triggerLocation: 'search_results_list',
    triggerObject: 'property_card',
  },
  map: {
    triggerLocation: 'search_results_map',
    triggerObject: 'property_card',
  },
  feed: {
    triggerLocation: 'search_results_feed',
    triggerObject: 'property_card',
  },
} as const;

export type ShareClickstreamContext = (typeof SHARE_CLICKSTREAM_CONTEXTS)[keyof typeof SHARE_CLICKSTREAM_CONTEXTS];

export const DEFAULT_LISTING_ACTION_ANALYTICS: ListingActionAnalyticsContext = {
  triggerLocation: 'home_details_halo',
  triggerObject: 'home_details_component|action_bar',
};

export function getListingActionId(listing: ListingLike | null | undefined): string | null {
  return getListingActionAlias(listing) ?? listing?.maloneLotIdEncoded ?? null;
}

export function getListingActionAlias(listing: ListingLike | null | undefined): string | null {
  return listing?.aliasEncoded ?? null;
}

export function getListingActionClickstreamListing(listing: unknown): HotpadsClickstreamListing | null {
  return toHotpadsClickstreamListing(listing);
}

export function listingHasFavoriteUserItem(listing: ListingLike | null | undefined): boolean {
  return listing?.userItemTypes?.includes('favorite') ?? false;
}

export function listingHasHiddenUserItem(listing: ListingLike | null | undefined): boolean {
  return listing?.userItemTypes?.includes('hidden') ?? false;
}

export function buildSavedListing(listing: ListingLike): SavedListing | null {
  const id = getListingActionId(listing);
  if (!id) return null;

  const price =
    listing.pricing?.summary?.priceLow ??
    listing.pricing?.summary?.priceHigh ??
    listing.listingMinMaxPriceBeds?.minPrice ??
    0;

  return {
    id,
    href: listing.uriMalone ?? listing.uriV2 ?? '#',
    title: listing.displayName ?? listing.title ?? listing.address?.street ?? 'Listing',
    price,
    imageUrl: listing.previewPhoto?.url ?? listing.medPhotoUrl ?? listing.medPhotoUrls?.[0] ?? '',
    bedDisplay: listing.bedText,
    bathDisplay: listing.bathText,
    neighborhood: listing.address?.neighborhood,
    latitude: listing.geo?.lat,
    longitude: listing.geo?.lon,
    savedAt: Date.now(),
  };
}
